import type { SportFilterMode } from '@/lib/kalshiTicker/sportFilter';
import { parseTicker } from '@/lib/kalshiTicker';
import type { TickerCategory } from '@/lib/kalshiTicker/types';
import { MLB_PROP_CATEGORIES } from '@/lib/kalshiTicker/sports/mlb';
import { NFL_PROP_CATEGORIES } from '@/lib/kalshiTicker/sports/nfl';
import { classifyPolyExtendedTicker } from '@/lib/kalshiTicker/polyPropsEvents';
import { useMarketStore } from '@/stores/marketStore';
import { marketKindFromTicker } from './marketKind';

// Position Tracker market-family filter (#980, QOL-07). A SEPARATE contract
// from `marketKindFromTicker`, which folds props, futures and unknowns into
// 'moneyline' and must keep doing so: the Value Dashboard routes focused
// deep links on it.
//
// Every classifier here returns `null` for PENDING — the row's market has no
// authoritative metadata yet (frame not ingested, pruned, or missing the
// field that decides). A pending row passes every chip: hiding a real money
// row behind a classification miss is the worse failure. A row whose
// metadata DID arrive but names no known family is 'other' and is filtered
// like any family.
export type TrackerMarketFamily = 'moneyline' | 'spread' | 'total' | 'props' | 'other';

export const TRACKER_FAMILY_CHIPS: ReadonlyArray<{ id: TrackerMarketFamily; label: string }> = [
  { id: 'moneyline', label: 'Moneyline' },
  { id: 'spread', label: 'Spread' },
  { id: 'total', label: 'Total' },
  { id: 'props', label: 'Props' },
  { id: 'other', label: 'Other' },
];

export type TrackerFamilyFilter = ReadonlyMap<TrackerMarketFamily, SportFilterMode>;

/** Proven game winners: one ticker per competitor for a single game/match
 *  (period winners — 1H, F5 — included). An allowlist, never a default:
 *  a category missing here must not silently read as Moneyline. */
const MONEYLINE_CATEGORIES: ReadonlySet<TickerCategory> = new Set<TickerCategory>([
  'nba-game',
  'wnba-game',
  'nfl-game',
  'nhl-game',
  'mlb-game',
  'ncaa-game',
  'cfb-game',
  'tennis-match',
  'ufc-fight',
  'boxing-fight',
  'esports-game',
  'cricket-match',
  'soccer-game',
]);

/** The soccer parser reuses 'soccer-game' for advance-to-next-round and
 *  both-teams-to-score markets, telling them apart only by this series tag
 *  (describeMarket relies on the ADVANCE tag too). Neither is a game winner. */
const SOCCER_NON_WINNER_SERIES: ReadonlySet<string> = new Set(['KXSOCCERADVANCE', 'KXSOCCERBTTS']);

const PROP_CATEGORIES: ReadonlySet<TickerCategory> = new Set<TickerCategory>([
  ...Object.values(MLB_PROP_CATEGORIES),
  ...Object.values(NFL_PROP_CATEGORIES),
  'mlb-rfi',
  'mlb-extras',
]);

/** Parsed categories that are known and belong to no game-level family:
 *  futures, season outrights, playoff series winners, non-sports. */
const OTHER_CATEGORIES: ReadonlySet<TickerCategory> = new Set<TickerCategory>([
  'nba-season',
  'wnba-season',
  'nfl-superbowl',
  'nba-series',
  'wnba-series',
  'nhl-series',
  'mlb-series',
  'politics',
  'crypto',
]);

/** Hydra's Kalshi series `class` for the non-prop families. */
const FAMILY_BY_KALSHI_CLASS: Readonly<Record<string, TrackerMarketFamily>> = {
  moneyline: 'moneyline',
  spread: 'spread',
  total: 'total',
  'team-total': 'total',
  btts: 'other',
  futures: 'other',
};

/** Player props and per-game event markets (CFB/NFL game props). */
const PROP_CLASSES: ReadonlySet<string> = new Set([
  'player-prop',
  'half-fulltime',
  'first-td-team',
  'first-to-score',
  'overtime',
  'overtime-count',
  'two-point',
  'every-quarter-btts',
  'highest-quarter',
  'team-stat',
  'total-touchdowns',
  'total-fieldgoals',
  'race-to',
  'both-teams-points',
]);

/** Any class string core ships that isn't listed is a resolved market of an
 *  unknown family → 'other'. */
function familyFromKalshiClass(cls: string): TrackerMarketFamily {
  if (PROP_CLASSES.has(cls)) return 'props';
  return FAMILY_BY_KALSHI_CLASS[cls] ?? 'other';
}

/** On Kalshi frames `marketType` is coarse — 'game' covers moneylines, lines
 *  and props alike, so it decides nothing. These values, though, are never a
 *  single-game winner (tournament winners ride 'advance'). */
const KALSHI_OTHER_MARKET_TYPES: ReadonlySet<string> = new Set(['future', 'advance', 'series', 'btts']);

/**
 * A Kalshi row's family. Spread/total keep exactly the classification the
 * chips always had (`marketKindFromTicker`); then the parsed category; then
 * hydra's `class` for tickers the parser can't read (CFB game props,
 * tournament futures). `null` = pending.
 */
export function kalshiTrackerFamily(ticker: string): TrackerMarketFamily | null {
  const kind = marketKindFromTicker(ticker);
  if (kind !== 'moneyline') return kind;
  const { category, series } = parseTicker(ticker);
  if (SOCCER_NON_WINNER_SERIES.has(series)) return 'other';
  if (MONEYLINE_CATEGORIES.has(category)) return 'moneyline';
  if (PROP_CATEGORIES.has(category)) return 'props';
  if (OTHER_CATEGORIES.has(category)) return 'other';
  const frame = useMarketStore.getState().getMarket(ticker);
  if (frame?.class) return familyFromKalshiClass(frame.class);
  if (frame?.marketType && KALSHI_OTHER_MARKET_TYPES.has(frame.marketType)) return 'other';
  return null;
}

/** Hydra's Polymarket `marketType`. Present-but-unlisted values are a
 *  resolved market of an unknown family → 'other'. */
const FAMILY_BY_POLY_MARKET_TYPE: Readonly<Record<string, TrackerMarketFamily>> = {
  moneyline: 'moneyline',
  game: 'moneyline',
  spread: 'spread',
  total: 'total',
  prop: 'props',
};

/** A Polymarket row's family from its hydra-synthesized PM ticker. The PM
 *  slug's prop grammar wins over `marketType` (the wire type is not
 *  authoritative for MLB props/events — see polyPropsEvents). */
function polyTrackerFamily(pmTicker: string): TrackerMarketFamily | null {
  if (classifyPolyExtendedTicker(pmTicker)) return 'props';
  const marketType = useMarketStore.getState().getMarket(pmTicker)?.marketType;
  if (!marketType) return null;
  return FAMILY_BY_POLY_MARKET_TYPE[marketType] ?? 'other';
}

/**
 * A Polymarket POSITION's family. Its ticker is an opaque hex conditionId, so
 * the family comes from the live frame it indexes to — never from the id.
 * Read LIVE, deliberately not memoized: the frame often arrives after first
 * paint, and a pruned market must degrade back to pending.
 */
export function polyPositionTrackerFamily(conditionId: string): TrackerMarketFamily | null {
  if (!conditionId) return null;
  const pmTicker = useMarketStore.getState().getTickerByConditionId(conditionId);
  return pmTicker ? polyTrackerFamily(pmTicker) : null;
}

/** A Polymarket ORDER's family, via its CLOB token (asset) id. Same live-read
 *  contract as {@link polyPositionTrackerFamily}. */
export function polyOrderTrackerFamily(assetId: string): TrackerMarketFamily | null {
  if (!assetId) return null;
  const pmTicker = useMarketStore.getState().getTickerByTokenId(assetId);
  return pmTicker ? polyTrackerFamily(pmTicker) : null;
}

/**
 * Include/exclude matching shared by the sport-less kind filters. Empty
 * filter passes everything; an excluded key is hidden; when any key is
 * included, only included keys pass. A `null` (pending) key always passes.
 */
export function familyMatchesFilter<K extends string>(
  key: K | null,
  filter: ReadonlyMap<K, SportFilterMode>,
): boolean {
  if (key == null || filter.size === 0) return true;
  const mode = filter.get(key);
  if (mode === 'exclude') return false;
  for (const m of filter.values()) {
    if (m === 'include') return mode === 'include';
  }
  return true;
}
